import { Injectable } from 'framework/di';

import { TemplateQueryParams, TemplateResponse } from '../../providers/boost';
import { BoostAPI } from '../app/app.api';
import { AppService } from '../app/app.service';
import { PlatformLoader } from '../platform/platform.loader';

@Injectable
export class TemplateAPI extends BoostAPI {
  CACHING_TTL = 86400000;

  constructor(
    private appService: AppService,
    platformLoader: PlatformLoader
  ) {
    super(platformLoader);
  }

  async getWidgetTemplate(widgets: Widget | Widget[]) {
    const {
      shop,
      templateMetadata: { themeNameLib },
    } = this.appService.TAEAppConfig;

    const widgetArray = Array.isArray(widgets) ? widgets : [widgets];
    const cachedWidgetTemplates = this.getCachedWidgetTemplates(widgetArray);
    const noneCachedWidgets = widgetArray.filter((widget) => !cachedWidgetTemplates[widget.id]);

    if (noneCachedWidgets.length === 0) {
      return {
        templates: cachedWidgetTemplates,
      };
    }

    const params: TemplateQueryParams = {
      shop: shop.domain || '',
      theme_lib: themeNameLib || 'default',
      widgets: noneCachedWidgets.map((widget) => `${widget.name}:${widget.id}`),
    };

    const requestURL = `${this.endpoint}?${this.buildDefaultQueryParams(params)}`;
    const {
      data: { templates },
    } = await this.get<TemplateResponse>(requestURL);

    this.cacheTemplates(templates);

    return {
      templates: {
        ...cachedWidgetTemplates,
        ...templates,
      },
    };
  }

  generateTemplateCacheKey(id: string) {
    return `WIDGET:${id}`;
  }

  getCachedWidgetTemplates(widgets: Widget | Widget[]) {
    const themeUpdatedAt = this.appService.TAEAppConfig.templateMetadata?.updatedAt || 0;
    const templates: Templates = {};

    const widgetArray = Array.isArray(widgets) ? widgets : [widgets];

    widgetArray.forEach((widget) => {
      if (!widget?.id) return;
      const key = this.generateTemplateCacheKey(widget.id.toString());
      const dataString = localStorage.getItem(key);
      if (dataString) {
        const data = JSON.parse(dataString);
        const isExpired = Date.now() - data.time > this.CACHING_TTL;
        const isOutDate = data.time / 1000 < themeUpdatedAt;
        if (isExpired || isOutDate) {
          localStorage.removeItem(key);
        } else {
          templates[widget.id] = data.template;
        }
      }
    });

    return templates;
  }

  cacheTemplates(templates: Templates) {
    Object.keys(templates).forEach((id) => {
      const key = this.generateTemplateCacheKey(id);
      const data = JSON.stringify({
        time: Date.now(),
        template: templates[id],
      });

      try {
        localStorage.setItem(key, data);
      } catch {
        // If localStorage is full, clear the cache and try again
        this.clearTemplateCache();
        localStorage.setItem(key, data);
      }
    });
  }

  clearTemplateCache() {
    const prefix = 'WIDGET:';

    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  }

  get endpoint() {
    if (!this.appService.TAEAppConfig.templateUrl) {
      throw new Error('Template URL is not defined in TAEAppConfig');
    }

    return this.appService.TAEAppConfig.templateUrl;
  }
}

type Templates = {
  [id: string]: string;
};

type Widget = {
  name: string;
  id: string | number;
};
